import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { isDebtAssignedToUser, isLoanOfficer } from "../accessControl";
import { formatMoney, getInlineLoader } from "../Helpers";
import { MdAssignmentInd, MdSearch } from "react-icons/md";
import OneDebt from "../ones/OneDebt";

export default function View(props) {
  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);
  const [publishedRecs, setPublishedRecs] = useState(null); // Active
  const [unpublishedRecs, setUnpublishedRecs] = useState(null); // Cleared
  const [cancelledRecs, setCancelledRecs] = useState(null); // Cancelled
  const [allocatedRecs, setAllocatedRecs] = useState(null);
  const [recsList, setRecsList] = useState(null);
  const [filter, setFilter] = useState("");
  const [category, setCategory] = useState("all");

  async function init() {
    setReady(false);
    await Promise.all([appContext.getAllDebts(), appContext.getAllCustomers()]);
    setReady(true);
  }

  function categorizeRecs() {
    if (appContext.allDebts) {
      const currentUser = appContext.currentUserData;
      const loanOfficerOnly = isLoanOfficer(currentUser);
      const scopedDebts = loanOfficerOnly
        ? appContext.allDebts.filter((item) => isDebtAssignedToUser(item, currentUser))
        : appContext.allDebts;

      const _published = scopedDebts.filter((item) => item.status === "active");
      const _unpublished = scopedDebts.filter((item) => item.status === "cleared");
      const _cancelled = scopedDebts.filter((item) => item.status === "cancelled");
      const _allocated = scopedDebts.filter((item) => isDebtAssignedToUser(item, currentUser));

      setPublishedRecs(_published);
      setUnpublishedRecs(_unpublished);
      setCancelledRecs(_cancelled);
      setAllocatedRecs(_allocated);
    }
  }

  function processRecs() {
    const loanOfficerOnly = isLoanOfficer(appContext.currentUserData);
    let _list = loanOfficerOnly ? allocatedRecs : appContext.allDebts;

    if (category === "cleared") {
      _list = unpublishedRecs;
    } else if (category === "active") {
      _list = publishedRecs;
    } else if (category === "cancelled") {
      _list = cancelledRecs;
    } else if (category === "allocated" || loanOfficerOnly) {
      _list = allocatedRecs;
    }

    if (filter && filter.trim().length > 0) {
      setRecsList(
        _list?.filter((item) => String(item.fullname).toLowerCase().indexOf(String(filter).toLowerCase()) > -1)
      );
    } else {
      setRecsList(_list);
    }
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    categorizeRecs();
  }, [appContext.allDebts]);

  useEffect(() => {
    processRecs();
  }, [filter, category, appContext.allDebts, publishedRecs, unpublishedRecs, cancelledRecs, allocatedRecs]);

  if (ready) {
    const loanOfficerOnly = isLoanOfficer(appContext.currentUserData);

    return (
      <div className="container">
        <div className="app-page">
          <div className="app-page-hero">
            <div>
              <h6 className="main-section-title">Debts</h6>
              <p className="app-page-subtitle">
                Review active loans, monitor repayments, and keep account balances clear for every motorcycle purchase.
              </p>
            </div>
          </div>

          <div className="row">
            <div className="col-6 col-md-3">
              <div className="summary-mini-card">
                <span>Total records</span>
                <strong>{loanOfficerOnly ? allocatedRecs?.length || 0 : appContext.allDebts?.length || 0}</strong>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="summary-mini-card">
                <span>Active</span>
                <strong>{publishedRecs?.length || 0}</strong>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="summary-mini-card">
                <span>Cleared</span>
                <strong className="text-success">{unpublishedRecs?.length || 0}</strong>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="summary-mini-card">
                <span>Cancelled</span>
                <strong className="text-danger">{cancelledRecs?.length || 0}</strong>
              </div>
            </div>
          </div>

          <div className="list-shell">
            {loanOfficerOnly ? (
              <div className="list-feedback d-flex align-items-center gap-2 mb-3">
                <MdAssignmentInd size={16} />
                <span>This view is limited to loan accounts assigned to you.</span>
              </div>
            ) : (
              ""
            )}

            <div className="list-toolbar">
              <div className="modern-search">
                <MdSearch size={20} />
                <input className="form-control" onChange={(e) => setFilter(e.target.value)} placeholder="Search debt accounts" />
              </div>

              <select className="form-control modern-filter" value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="all">{loanOfficerOnly ? "Assigned records" : "All records"}</option>
                <option value="active">Active</option>
                <option value="cleared">Cleared</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            {filter && filter.trim().length > 0 ? <div className="list-feedback">Results for "{filter}"</div> : ""}

            <div className="entity-list">
              {recsList && recsList.length > 0 ? recsList.map((item, i) => <OneDebt key={i} data={item} />) : <div className="empty-state-lite">No debt records were found.</div>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <div className="container mSupportLoading">{getInlineLoader()}</div>;
}